import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { axiosInstance } from "../App";
import { FileText, CheckCircle } from "lucide-react";
import { toast } from "sonner";

function TemplateSelector({ projectId, onTaskCreated, trigger }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [open]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/templates");
      setTemplates(response.data);
    } catch (error) {
      toast.error("Şablonlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFromTemplate = async () => {
    if (!selectedTemplate) {
      toast.error("Lütfen bir şablon seçin");
      return;
    }

    setCreating(true);
    try {
      await axiosInstance.post(`/templates/${selectedTemplate}/create-task`, {
        project_id: projectId
      });
      toast.success("Görev şablondan oluşturuldu");
      setOpen(false);
      setSelectedTemplate(null);
      if (onTaskCreated) onTaskCreated();
    } catch (error) {
      toast.error("Görev oluşturulamadı");
    } finally {
      setCreating(false);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: "bg-red-100 text-red-800",
      medium: "bg-yellow-100 text-yellow-800",
      low: "bg-green-100 text-green-800"
    };
    return colors[priority] || colors.medium;
  };

  const getPriorityLabel = (priority) => {
    const labels = { high: "Yüksek", medium: "Orta", low: "Düşük" };
    return labels[priority] || priority;
  };

  const selectedTemplateData = templates.find(t => t.id === selectedTemplate);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <div onClick={() => setOpen(true)}>{trigger}</div>}
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Şablondan Görev Oluştur</DialogTitle>
          <DialogDescription>
            Kayıtlı şablonlarınızdan birini seçerek hızlıca görev oluşturun
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {loading ? (
            <p className="text-center text-gray-500 py-8">Şablonlar yükleniyor...</p>
          ) : templates.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-600">Henüz şablonunuz yok</p>
              <p className="text-sm text-gray-500 mt-1">
                Şablonlar sayfasından yeni şablon oluşturabilirsiniz
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium">Şablon Seç</label>
                <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  <SelectTrigger>
                    <SelectValue placeholder="Bir şablon seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedTemplateData && (
                <div className="p-4 border rounded-lg bg-gray-50">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-blue-500" />
                    Şablon Önizleme
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Görev Başlığı:</span>
                      <p className="text-gray-600">{selectedTemplateData.title}</p>
                    </div>
                    {selectedTemplateData.task_description && (
                      <div>
                        <span className="font-medium text-gray-700">Açıklama:</span>
                        <p className="text-gray-600">{selectedTemplateData.task_description}</p>
                      </div>
                    )}
                    <div className="flex items-center gap-2 pt-2">
                      <Badge className={getPriorityColor(selectedTemplateData.priority)} variant="secondary">
                        {getPriorityLabel(selectedTemplateData.priority)}
                      </Badge>
                      {selectedTemplateData.tags && selectedTemplateData.tags.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {selectedTemplateData.tags.map((tag, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              #{tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    {selectedTemplateData.checklist && selectedTemplateData.checklist.length > 0 && (
                      <div className="pt-2">
                        <span className="font-medium text-gray-700">Checklist:</span>
                        <ul className="mt-1 space-y-1">
                          {selectedTemplateData.checklist.slice(0, 3).map((item, idx) => (
                            <li key={idx} className="text-gray-600 flex items-center gap-2">
                              <CheckCircle className="h-3 w-3" />
                              {item.text}
                            </li>
                          ))}
                          {selectedTemplateData.checklist.length > 3 && (
                            <li className="text-gray-500 text-xs">
                              +{selectedTemplateData.checklist.length - 3} öğe daha
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleCreateFromTemplate}
                  disabled={!selectedTemplate || creating}
                  className="flex-1"
                >
                  {creating ? "Oluşturuluyor..." : "Oluştur"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setOpen(false)}
                  disabled={creating}
                >
                  İptal
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TemplateSelector;
